import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@/core/services/api/api.service';
import {
  ActiviteCredit,
  AuditLog,
  CreditAnalyseDemandeDetail,
  CreditClientDetail,
  CreditDemande,
  CreditDocumentAnnexe,
  CreditFiche,
  CreditFicheDemandeDetail,
  CreditObservation,
  CreditResume,
  CreditSaveBonCommande,
  CreditSaveDemande,
  CreditSaveFacture,
  CreditSaveMagasin,
  CreditSuperviseurPmeAffectation,
  CreditTypeActivite,
  CreditTypeCredit,
  CreditUpdateDemande,
  CreditZone,
  CreditAnalysteRisque,
  CreditResumeHistoriqueDemande,
  ResponseDocumentsObligatoireAndActions,
} from '../../interfaces/credit.interface';
import { Tirage } from '../../interfaces/tirage.interface';

@Injectable({ providedIn: 'root' })
export class CreditDemandeService {
  private readonly api = inject(ApiService);
  private readonly endpoint = '/credit';

  // ── Référentiels ─────────────────────────────────────────────────────────
  getTypesCredit() {
    return this.api
      .get<{ typeCredits: CreditTypeCredit[] }>(this.endpoint + '/listeTypeCredit')
      .pipe(
        map((res) => res.typeCredits),
        catchError((err) => throwError(() => err)),
      );
  }

  getTypesActivite() {
    return this.api
      .get<{ TypeActivites: CreditTypeActivite[] }>(this.endpoint + '/gettypeactivite')
      .pipe(
        map((res) => res.TypeActivites),
        catchError((err) => throwError(() => err)),
      );
  }

  getPaysCommuneData() {
    return this.api
      .get<{
        pays: { id: number; nationalite: string }[];
        villes: { id: number; libelle: string }[];
        communes: { id: number; libelle: string }[];
      }>('/pays_commune')
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Zones / ARs / Superviseurs ────────────────────────────────────────────
  getZones() {
    return this.api.get<{ zones: CreditZone[] }>('/zones').pipe(
      map((response) => response.zones || []),
      catchError((err) => throwError(() => err)),
    );
  }

  getARsByZone(zoneId: number) {
    return this.api.get<{ ars: CreditAnalysteRisque[] }>(`/get_ars_by_zone/${zoneId}`).pipe(
      map((response) => response.ars || []),
      catchError((err) => throwError(() => err)),
    );
  }

  getSuperviseursPmeEtChefAgence() {
    return this.api
      .get<{ superviseuPmes: CreditSuperviseurPmeAffectation[] }>('/users/supAndCa')
      .pipe(
        map((response) => response.superviseuPmes || []),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Client ────────────────────────────────────────────────────────────────
  searchClientCredit(codeClient: string) {
    return this.api
      .post<{ client: CreditClientDetail }>(this.endpoint + '/searchClient', { codeClient })
      .pipe(
        map((res) => res.client),
        catchError((err) => throwError(() => err)),
      );
  }

  updateClientPP(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/update_client', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateClientPM(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/update_client_entreprise', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Liste & Détail demandes ───────────────────────────────────────────────
  getListeDemandes(action?: 'cloture' | 'rejette') {
    let params = new HttpParams();
    if (action) params = params.set('action', action);
    return this.api.get<{ demande: CreditDemande[] }>(this.endpoint + '/listeDemande', params).pipe(
      map((res) => res.demande ?? []),
      catchError((err) => throwError(() => err)),
    );
  }

  getFicheCredit(ref: string) {
    return this.api
      .get<CreditFiche>(this.endpoint + '/getinfosdmde/' + ref)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getDetailsDemande(ref: string) {
    return this.api
      .get<{ demande: CreditFicheDemandeDetail }>(this.endpoint + '/getDetailsDemande/' + ref)
      .pipe(
        map((res) => res.demande),
        catchError((err) => throwError(() => err)),
      );
  }

  getResumeAnalyse(ref: string) {
    return this.api
      .get<CreditResume>(this.endpoint + '/getresume/' + ref)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getAnalyseFinanciere(ref: string) {
    return this.api
      .get<{
        data?: { demande: CreditAnalyseDemandeDetail };
        demande?: CreditAnalyseDemandeDetail;
      }>(this.endpoint + '/get_demande/' + ref)
      .pipe(
        map((res) => ({
          demande: (res.data?.demande ?? res.demande) as CreditAnalyseDemandeDetail,
        })),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Observations & Documents ──────────────────────────────────────────────
  getObservations(ref: string) {
    return this.api
      .get<{ observations: CreditObservation[] }>(this.endpoint + '/listeObservations/' + ref)
      .pipe(
        map((res) => res.observations ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  getDocuments(ref: string) {
    return this.api
      .get<{ documents: CreditDocumentAnnexe[] }>(this.endpoint + '/listeDocument/' + ref)
      .pipe(
        map((res) => res.documents ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  uploadDocument(formData: FormData) {
    return this.api
      .postFormData<{
        status: number;
        message?: string;
      }>(this.endpoint + '/saveDocAnnexe', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteDocument(idDocument: number | string) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/documents/' + idDocument)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Création / modification demande ──────────────────────────────────────
  saveDemandeCredit(data: CreditSaveDemande) {
    return this.api
      .post<{
        status: number;
        demande: { refDemande: string };
        message?: string;
      }>(this.endpoint + '/saveDemandeCredit', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateDemandeCredit(data: CreditUpdateDemande) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/updateDemandeCredit', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Pièces jointes à la demande ───────────────────────────────────────────
  saveMagasin(data: CreditSaveMagasin) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrMagasin', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveFacture(data: CreditSaveFacture) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrFacture', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveBonCommande(data: CreditSaveBonCommande) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveBonCmde', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Véhicules (Crédit Auto) ───────────────────────────────────────────────
  saveInfoCrAuto(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveInfoCrAuto', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteInfoCrAuto(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteCrAuto/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Signataires ───────────────────────────────────────────────────────────
  recupererSignataire(codeClient: string) {
    return this.api
      .get<{ status: number; message?: string }>(this.endpoint + '/getSignataire/' + codeClient)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateSignataire(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/updtateSignataire', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateSignataireAvecPhoto(formData: FormData) {
    return this.api
      .postFormData<{
        status: number;
        message?: string;
      }>(this.endpoint + '/updateProfilImgCaution', formData)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Activités ─────────────────────────────────────────────────────────────
  getActivitesDemande(ref: string) {
    return this.api
      .get<{ activite?: ActiviteCredit[] }>(this.endpoint + '/getActiviteDemande/' + ref)
      .pipe(
        map((res) => res.activite ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Pré-évaluation ACJ / CE ──────────────────────────────────────────────
  savePreEvaluation(data: Record<string, unknown>) {
    return this.api.post<{ status: number; message?: string }>(
      this.endpoint + '/savePreEvaluation',
      data,
    );
  }

  // ── Audit logs ───────────────────────────────────────────────────────────
  getLogs(ref: string, type: 'documents' | 'modifications' | 'ajouts') {
    const action = type === 'ajouts' ? 'created' : 'updated';
    const params = new HttpParams()
      .set('module', 'credit')
      .set('reference', ref)
      .set('action', action)
      .set('type', type);
    return this.api
      .get<{ statut: boolean; data: AuditLog[] }>(this.endpoint + '/getLogs', params)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Tirages ───────────────────────────────────────────────────────────────
  getHistoriqueTirages(ref: string) {
    return this.api
      .get<{ tirages: Tirage[] }>(this.endpoint + '/listeTirage?refDemande=' + ref)
      .pipe(
        map((res) => res.tirages ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Historique demande ───────────────────────────────────────────────────────
  getHistoriqueDemande(
    codeClient?: string | null,
    params?: {
      /** `oui` : crédits déboursés uniquement · `non` : non déboursés · omis : tous. */
      debourse?: 'oui' | 'non';
    }
  ) {
    const queryParams = new HttpParams({ fromObject: params });
    return this.api
      .get<{ demandes: CreditResumeHistoriqueDemande[] }>(this.endpoint + '/getHistoriqueDemande/' + (codeClient ?? ""), queryParams)
      .pipe(
        map((res) => res.demandes ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  // --- Documents obligatoire et actions -----------------------------------------------------
  getDocumentsObligatoireAndActions(ref: string) {
    return this.api
      .get<ResponseDocumentsObligatoireAndActions>(this.endpoint + '/demandes/' + ref + '/ecran')
      .pipe(
        catchError((err) => throwError(() => err)),
      );
  }
}
